import type { IsolatedPartnerPointsPerPair } from '@/config/chain/types'
import { assets } from '@/ui/assets'
import { Tooltip, TooltipContentLong, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { TooltipContentLayout } from '@/ui/atoms/tooltip/TooltipContentLayout'
import { Typography } from '@/ui/atoms/typography/Typography'
import { Info } from 'lucide-react'
import Image from 'next/image'
import { ReactNode } from 'react'

export type ApyMarketType = 'evault' | 'elend' | 'isolated' | 'pooled'

interface SupplyApyBreakdown {
  lendingApy: number
  intrinsicApy?: number
  totalApy: number
}

interface PointsInfo {
  multiplier?: number
  partnerPoints?: IsolatedPartnerPointsPerPair[]
  asset: string
}

interface SupplyApyTooltipProps {
  children: ReactNode
  breakdown: SupplyApyBreakdown
  collateralOnly?: boolean
  points?: PointsInfo
  marketType?: ApyMarketType
  showPointsSection?: boolean
  vaultAddress?: string
}

const LENDING_DESCRIPTIONS: Record<ApyMarketType, string> = {
  evault: 'Yield from lending via vault strategies',
  elend: 'Yield from borrower interest payments',
  isolated: 'Yield from borrower interest in this isolated pair',
  pooled: 'Yield from borrower interest in the shared pool',
}

function formatApy(value: number): string {
  if (value > 0 && value < 0.005) return '<0.01%'
  return `${value.toFixed(2)}%`
}

interface AssetProviderInfo {
  label: string
  url: string
}

function normalizeVaultAddressMap<T>(map: Record<string, T>): Record<string, T> {
  return Object.fromEntries(Object.entries(map).map(([address, value]) => [address.toLowerCase(), value]))
}

const ASSET_PROVIDER_BY_VAULT: Record<string, AssetProviderInfo> = normalizeVaultAddressMap({
  '0xac207ffa0bfce194db18d1820902d48a2d948968': {
    label: 'Parallel',
    url: 'https://app.parallel.best/earn',
  },
  '0x23bf20b4d6E280eacA58826a541c9eE5401BD357': {
    label: 'Looping Collective',
    url: 'https://app.loopingcollective.org/product/lhype',
  },
  '0x64a3052570F5A1c241C6c8cd32F8F9aD411e6990': {
    label: 'Valantis',
    url: 'https://app.valantis.xyz/staking',
  },
  '0xBb7DC37dbc108d40BcdD60403EF7bFDD6489071E': {
    label: 'Hyperwave',
    url: 'https://app.hyperwavefi.xyz/vaults/hwhype',
  },
  '0x443100d1149D6d925Edb044248BBE32c5C7Ae955': {
    label: 'Kinetiq',
    url: 'https://kinetiq.xyz/',
  },
  '0x97d30B40048bA3fC6b6628cE5E02E77f35B64fE0': {
    label: 'Kinetiq',
    url: 'https://kinetiq.xyz/',
  },
})

export function SupplyApyTooltip({
  children,
  breakdown,
  collateralOnly = false,
  points,
  marketType = 'elend',
  showPointsSection = true,
  vaultAddress,
}: SupplyApyTooltipProps) {
  const { lendingApy, intrinsicApy, totalApy } = breakdown
  const hasIntrinsicApy = intrinsicApy !== undefined && intrinsicApy > 0
  const hasLendingApy = lendingApy > 0
  const hasPoints = points?.multiplier !== undefined
  const partnerConfig = points?.partnerPoints?.[0]
  const hasPartnerPoints = partnerConfig?.lend || partnerConfig?.deposit || partnerConfig?.collateral
  const isEarnVault = marketType === 'evault'
  const assetProvider = vaultAddress ? ASSET_PROVIDER_BY_VAULT[vaultAddress.toLowerCase()] : undefined

  const showBreakdown = hasLendingApy || hasIntrinsicApy

  return (
    <Tooltip>
      <TooltipTrigger
        asChild
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        <div className="cursor-help">{children}</div>
      </TooltipTrigger>
      <TooltipContentLong forceMount className="sm:w-[600px] sm:max-w-xs">
        <TooltipContentLayout>
          {hasLendingApy && (
            <div className="border-border border-b pb-3">
              <div className="mb-1 flex items-center justify-between">
                <Typography variant="h4" className="font-medium text-sm leading-none">
                  Lending APY
                </Typography>
                <Typography className="font-bold leading-none">{formatApy(lendingApy)}</Typography>
              </div>

              <TooltipContentLayout.Body>
                <Typography variant="p" className="text-muted-foreground text-xs">
                  {LENDING_DESCRIPTIONS[marketType]}
                </Typography>
              </TooltipContentLayout.Body>
            </div>
          )}

          {collateralOnly && (
            <TooltipContentLayout.Body>
              <div className="border-border border-b pb-3">
                <div className="flex items-start gap-1.5 text-sm">
                  <Info size={14} className="flex-shrink-0 translate-y-0.5" />
                  <Typography variant="p" className="text-muted-foreground text-xs">
                    This asset is collateral only and does not earn borrowing-driven APR.
                  </Typography>
                </div>
              </div>
            </TooltipContentLayout.Body>
          )}

          {hasIntrinsicApy && (
            <div className="border-border border-b pb-3">
              <div className="mb-1 flex items-center justify-between">
                <Typography variant="h4" className="font-medium text-sm leading-none">
                  Intrinsic APY
                </Typography>
                <Typography className="font-bold text-green-400 leading-none">
                  {hasLendingApy ? '+ ' : ''}
                  {formatApy(intrinsicApy)}
                </Typography>
              </div>

              <TooltipContentLayout.Body>
                <div>
                  <Typography variant="p" className="text-muted-foreground text-xs">
                    Yield intrinsic to the supplied asset, such as staking yield or external rewards.
                  </Typography>
                  {assetProvider && (
                    <span className="text-muted-foreground text-xs">
                      Asset provider:{' '}
                      <a
                        href={assetProvider.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-white underline underline-offset-2"
                      >
                        {assetProvider.label}
                      </a>
                    </span>
                  )}
                </div>
              </TooltipContentLayout.Body>
            </div>
          )}

          <div className="mb-1 flex items-center justify-between">
            <Typography variant="h4" className="font-medium text-sm leading-none">
              {showBreakdown ? 'Total Supply APY' : 'Supply APY'}
            </Typography>
            <Typography className="font-bold leading-none">{formatApy(totalApy)}</Typography>
          </div>

          {showPointsSection && (hasPoints || hasPartnerPoints || isEarnVault) && (
            <div className="border-border border-t pt-3">
              <TooltipContentLayout.Header>
                <TooltipContentLayout.Title>Eligible for points</TooltipContentLayout.Title>
              </TooltipContentLayout.Header>
              <TooltipContentLayout.Body>
                {isEarnVault && (
                  <span className="text-sm">
                    This vault is earning points in accordance with the strategy utilized. Check the underlying lending
                    markets for clarity on points multipliers.
                  </span>
                )}
                {hasPoints && (
                  <div className="mt-2 flex items-center gap-2">
                    <Image src={assets.hypurrPawNoShadow} alt="HP" className="h-4 w-4" />
                    <span className="text-sm">
                      {points.multiplier ? `${points.multiplier}x ` : ''}HypurrFi Points on {points.asset}
                    </span>
                  </div>
                )}
                {hasPartnerPoints && partnerConfig && (
                  <div className="mt-2 flex items-center gap-2">
                    <Image
                      src={assets[partnerConfig.icon as keyof typeof assets]}
                      alt={partnerConfig.title}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">
                      {partnerConfig.lend?.multiplier ||
                      partnerConfig.deposit?.multiplier ||
                      partnerConfig.collateral?.multiplier
                        ? `${partnerConfig.lend?.multiplier || partnerConfig.deposit?.multiplier || partnerConfig.collateral?.multiplier}x `
                        : ''}
                      {partnerConfig.title}
                    </span>
                  </div>
                )}
              </TooltipContentLayout.Body>
            </div>
          )}
        </TooltipContentLayout>
      </TooltipContentLong>
    </Tooltip>
  )
}
